import type { MutableRefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { makeStyles } from '@mui/styles';
import { useWindowSize } from '@project/common/app/hooks/use-window-size';
import type {
    SubtitleModel,
    AudioTrackModel,
    OffscreenDomCache,
    CardTextFieldValues,
    ControlType,
    IndexedSubtitleModel,
    PlaybackState,
} from '@project/common';
import { PostMineAction, PlayMode, PostMinePlayback } from '@project/common';
import type {
    MiscSettings,
    SubtitleSettings,
    AnkiSettings,
    AsbplayerSettings,
    SubtitleAlignment,
    DictionaryTrack,
    SettingsProvider,
} from '@project/common/settings';
import {
    changeForTextSubtitleSetting,
    subtitlesWidthCssValue,
    textSubtitleSettingsForTrack,
    PauseOnHoverMode,
    allTextSubtitleSettings,
    TokenState,
    ApplyStrategy,
} from '@project/common/settings';
import {
    adjacentSubtitle,
    arrayEquals,
    compareSubtitlesForDisplay,
    surroundingSubtitles,
    mockSurroundingSubtitles,
    seekWithNudge,
    surroundingSubtitlesAroundInterval,
    ensureStoragePersisted,
    subtitleTimestampWithDelay,
    errorMessageFromVideo,
    formatAsSignedMs,
    mapSubtitlesForDisplay,
    timeDurationDisplay,
} from '@project/common/util';
import { asbTrace } from '@project/common/util/log';
import {
    HoveredToken,
    renderRichTextOntoSubtitles,
    getAnnotationsHtml,
    ASB_SUBTITLE_INDEX_ATTRIBUTE,
} from '@project/common/annotations';
import type { TokenJumpMatch } from '@project/common/annotations/token-navigation';
import Clock from '@project/common/playback/timing/clock';
import {
    formatPlaybackModeNotifications,
    playbackModeNotificationJoin,
} from '@project/common/playback/controllers/playback-mode-controller';
import type {
    PlaybackModeNotificationFormatOptions,
    PlayModeTransition,
} from '@project/common/playback/controllers/playback-mode-controller';
import PlaybackEngine from '@project/common/playback/playback-engine';
import VideoFrameTimingDriver from '@project/common/playback/timing/video-frame-timing-driver';
import type { Point } from '@project/common/app/components/Controls';
import Controls from '@project/common/app/components/Controls';
import PlayerChannel from '@project/common/app/services/player-channel';
import type ChromeExtension from '@project/common/app/services/chrome-extension';
import Alert from '@project/common/app/components/Alert';
import type { AlertNotification } from '@project/common/app/components/Alert';
import Button from '@mui/material/Button';
import { useSubtitleDomCache } from '@project/common/app/hooks/use-subtitle-dom-cache';
import { useAppKeyBinder } from '@project/common/app/hooks/use-app-key-binder';
import type { Direction } from '@project/common/app/hooks/use-swipe';
import { useSwipe } from '@project/common/app/hooks/use-swipe';
import '@project/common/app/components/subtitles.css';
import i18n from 'i18next';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { usePlaybackPreferences } from '@project/common/app/hooks/use-playback-preferences';
import type { MiningContext } from '@project/common/app/services/mining-context';
import useSnackbar from '@project/common/hooks/use-snackbar';
import { useStableDictionaryTracks, useSubtitleStyles } from '@project/common/app/hooks/use-subtitle-styles';
import { useTokenSelection } from '@project/common/app/hooks/use-token-selection';
import { useFullscreen } from '@project/common/app/hooks/use-fullscreen';
import MobileVideoOverlay from '@project/common/components/MobileVideoOverlay';
import BlurOverlay from '@project/common/app/components/BlurOverlay';
import { CachedLocalStorage } from '@project/common/app/services/cached-local-storage';
import useLastScrollableControlType from '@project/common/hooks/use-last-scrollable-control-type';
import type { Theme } from '@mui/material/styles';

const overlayContainerHeight = 48;
interface ExperimentalHTMLVideoElement extends HTMLVideoElement {
    readonly audioTracks: any;
}

const useStyles = makeStyles<Theme>((theme) => ({
    root: {
        position: 'relative',
        backgroundColor: 'black',
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    video: {
        margin: 'auto',
    },
    cursorHidden: {
        cursor: 'none',
    },
    mobileOverlay: {
        position: 'absolute',
        zIndex: 10,
        bottom: theme.spacing(1.5),
    },
}));

function notifyReady(
    element: ExperimentalHTMLVideoElement,
    playerChannel: PlayerChannel,
    setAudioTracks: React.Dispatch<React.SetStateAction<AudioTrackModel[] | undefined>>,
    setSelectedAudioTrack: React.Dispatch<React.SetStateAction<string | undefined>>
) {
    if (window.outerWidth && element.videoWidth > 0 && element.videoHeight > 0) {
        const availWidth = window.screen.availWidth - (window.outerWidth - window.innerWidth);
        const availHeight = window.screen.availHeight - (window.outerHeight - window.innerHeight);
        const resizeRatio = Math.min(1, Math.min(availWidth / element.videoWidth, availHeight / element.videoHeight));

        window.resizeTo(
            resizeRatio * element.videoWidth + (window.outerWidth - window.innerWidth),
            resizeRatio * element.videoHeight + (window.outerHeight - window.innerHeight)
        );
    }

    let tracks: AudioTrackModel[] | undefined;
    let selectedTrack: string | undefined;

    if (element.audioTracks) {
        tracks = [];

        for (const t of element.audioTracks) {
            tracks.push({
                id: t.id,
                label: t.label,
                language: t.language,
            });

            if (t.enabled) {
                selectedTrack = t.id;
            }
        }
    } else {
        tracks = undefined;
        selectedTrack = undefined;
    }

    setAudioTracks(tracks);
    setSelectedAudioTrack(selectedTrack);
    asbTrace('playback/video-player', 'Video media is ready', {
        durationMs: element.duration * 1000,
        currentTimeMs: element.currentTime * 1000,
        paused: element.paused,
        playbackRate: element.playbackRate,
        readyState: element.readyState,
        videoWidth: element.videoWidth,
        videoHeight: element.videoHeight,
        audioTrackCount: tracks?.length ?? 0,
        hasSelectedAudioTrack: selectedTrack !== undefined,
    });
    playerChannel.ready(element.duration, element.paused, element.playbackRate, tracks, selectedTrack);
}

const showingSubtitleHtml = (
    subtitle: IndexedSubtitleModel,
    videoRef: MutableRefObject<ExperimentalHTMLVideoElement | undefined>,
    subtitleStyles: string,
    subtitleClasses: string,
    imageBasedSubtitleScaleFactor: number,
    dictionaryTracks: DictionaryTrack[]
) => {
    if (subtitle.textImage) {
        const imageScale =
            (imageBasedSubtitleScaleFactor * (videoRef.current?.width ?? window.screen.availWidth)) /
            subtitle.textImage.screen.width;
        const width = imageScale * subtitle.textImage.image.width;
        return `
<div style="max-width:${width}px;margin:auto;">
<img
    style="width:100%;"
    alt="subtitle"
    src="${subtitle.textImage.dataUrl}"
    class="${subtitleClasses}"
/>
</div>
`;
    }
    const allSubtitleClasses = subtitleClasses ? `${subtitleClasses} asbplayer-subtitles` : 'asbplayer-subtitles';
    const rendered = renderRichTextOntoSubtitles([subtitle], 'video', dictionaryTracks)?.get(subtitle.index);
    return `<span class="${allSubtitleClasses}" style="${subtitleStyles}" data-track="${subtitle.track}" ${ASB_SUBTITLE_INDEX_ATTRIBUTE}="${subtitle.index}">${getAnnotationsHtml(
        subtitle.text,
        rendered?.richText,
        rendered?.richTextOnHover
    )}</span>`;
};

interface CachedShowingSubtitleProps {
    subtitle: IndexedSubtitleModel;
    visible: boolean;
    domCache: OffscreenDomCache;
    renderHtml: (subtitle: IndexedSubtitleModel) => string;
    className?: string;
    onMouseOver: React.MouseEventHandler<HTMLDivElement>;
    onMouseOut: React.MouseEventHandler<HTMLDivElement>;
}

const CachedShowingSubtitle = React.memo(function CachedShowingSubtitle({
    subtitle,
    visible,
    domCache,
    renderHtml,
    className,
    onMouseOver,
    onMouseOut,
}: CachedShowingSubtitleProps) {
    return (
        <div
            className={className ? className : ''}
            aria-hidden={!visible}
            style={visible ? { pointerEvents: 'auto' } : { visibility: 'hidden', pointerEvents: 'none' }}
            onMouseOver={onMouseOver}
            onMouseOut={onMouseOut}
            ref={(ref) => {
                if (!ref) {
                    return;
                }

                while (ref.firstChild) {
                    domCache.return(ref.lastChild! as HTMLElement);
                }

                ref.appendChild(domCache.get(String(subtitle.index), () => renderHtml(subtitle)));
            }}
        />
    );
});

const useSubtitleContainerStyles = makeStyles(() => ({
    subtitleContainer: {
        position: 'absolute',
        zIndex: 6,
        maxWidth: '100%',
        paddingLeft: 20,
        paddingRight: 20,
        textAlign: 'center',
        whiteSpace: 'normal',
        lineHeight: 'inherit',
    },
}));

interface SubtitleContainerProps {
    subtitleSettings: SubtitleSettings;
    alignment: SubtitleAlignment;
    subtitleZIndex: boolean;
    baseOffset: number;
    children: React.ReactNode;
}

const SubtitleContainer = React.forwardRef<HTMLDivElement, SubtitleContainerProps>(function SubtitleContainer(
    { subtitleSettings, alignment, baseOffset, children, subtitleZIndex }: SubtitleContainerProps,
    ref
) {
    const classes = useSubtitleContainerStyles();
    const width = subtitlesWidthCssValue(subtitleSettings);
    return (
        <div
            ref={ref}
            className={classes.subtitleContainer}
            style={{
                ...(alignment === 'bottom'
                    ? { bottom: subtitleSettings.subtitlePositionOffset + baseOffset }
                    : { top: subtitleSettings.topSubtitlePositionOffset + baseOffset }),
                ...(width === undefined ? {} : { width }),
                zIndex: subtitleZIndex ? 12 : 0,
                pointerEvents: 'none',
            }}
        >
            {children}
        </div>
    );
});

export interface SeekRequest {
    timestamp: number;
}

interface Props {
    settings: AsbplayerSettings;
    settingsProvider: SettingsProvider;
    extension: ChromeExtension;
    videoFile: string;
    channel: string;
    popOut: boolean;
    miningContext: MiningContext;
    ankiDialogOpen: boolean;
    seekRequest?: SeekRequest;
    onAnkiDialogRequest: (
        videoFileUrl: string,
        videoFileName: string,
        selectedAudioTrack: string | undefined,
        playbackRate: number,
        subtitle: SubtitleModel,
        surroundingSubtitles: SubtitleModel[],
        cardTextFieldValues: CardTextFieldValues,
        timestamp: number
    ) => void;
    onSettingsChanged: (settings: Partial<AsbplayerSettings>) => void;
    profile?: string;
    onAnkiDialogRewind: () => void;
    onError: (error: string) => void;
}

interface MinedRecord {
    videoFileUrl: string;
    videoFileName: string;
    selectedAudioTrack: string | undefined;
    playbackRate: number;
    subtitle: SubtitleModel;
    surroundingSubtitles: SubtitleModel[];
    timestamp: number;
}

const allSubtitleAlignments = (subtitleSettings: SubtitleSettings) => {
    return allTextSubtitleSettings(subtitleSettings).map((s) => s.subtitleAlignment);
};

const lastControlTypeKey = 'lastScrollableControlType';
const storage = new CachedLocalStorage();

const fetchLastControlType = async (): Promise<ControlType | undefined> => {
    const val = storage.get(lastControlTypeKey);

    if (val == null) {
        return undefined;
    }

    return parseInt(val);
};

const saveLastControlType = async (controlType: ControlType): Promise<void> => {
    storage.set(lastControlTypeKey, String(controlType));
};

export default function VideoPlayer({
    settings,
    settingsProvider,
    extension,
    videoFile,
    channel,
    popOut,
    miningContext,
    ankiDialogOpen,
    seekRequest,
    onAnkiDialogRequest,
    onError,
    onAnkiDialogRewind,
    onSettingsChanged,
    profile,
}: Props) {
    const classes = useStyles();
    const { t } = useTranslation();
    const poppingInRef = useRef<boolean>(undefined);
    const onSettingsChangedRef = useRef(onSettingsChanged);
    onSettingsChangedRef.current = onSettingsChanged;
    const videoRef = useRef<ExperimentalHTMLVideoElement>(undefined);
    const [video, setVideo] = useState<ExperimentalHTMLVideoElement>();
    const playbackEngineRef = useRef<PlaybackEngine<IndexedSubtitleModel>>(undefined);
    const profileRef = useRef(profile);
    const hiddenVideoRef = useRef<HTMLVideoElement | null>(null); // seek preview thumbnail
    const [hiddenVideoReady, setHiddenVideoReady] = useState(false);
    const [windowWidth, windowHeight] = useWindowSize(true);
    if (videoRef.current) {
        videoRef.current.width = windowWidth;
        videoRef.current.height = windowHeight;
    }
    const playerChannel = useMemo(() => {
        asbTrace('playback/video-player', 'Creating player channel');
        return new PlayerChannel(channel);
    }, [channel]);
    const [playerChannelSubscribed, setPlayerChannelSubscribed] = useState<boolean>(false);
    const { fullscreen, requestFullscreen } = useFullscreen();
    const playing = () => !videoRef.current?.paused || false;
    const [lengthMs, setLengthMs] = useState<number>(0);
    const lengthMsRef = useRef(lengthMs);
    lengthMsRef.current = lengthMs;
    const [videoFileName, setVideoFileName] = useState<string>();
    const videoFileNameRef = useRef(videoFileName);
    videoFileNameRef.current = videoFileName;
    const [videoWidth, setVideoWidth] = useState<number>(); // width and height are original width and height from metadata
    const [videoHeight, setVideoHeight] = useState<number>();
    const [offset, setOffset] = useState<number>(0);
    const offsetRef = useRef(offset);
    offsetRef.current = offset;
    const [audioTracks, setAudioTracks] = useState<AudioTrackModel[]>();
    const [selectedAudioTrack, setSelectedAudioTrack] = useState<string>();
    const [wasPlayingOnAnkiDialogRequest, setWasPlayingOnAnkiDialogRequest] = useState<boolean>(false);
    const [subtitles, setSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const subtitlesRef = useRef(subtitles);
    subtitlesRef.current = subtitles;
    const [showSubtitles, setShowSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const [invisibleSubtitles, setInvisibleSubtitles] = useState<IndexedSubtitleModel[]>([]);
    const [miscSettings, setMiscSettings] = useState<MiscSettings>(settings);
    const miscSettingsRef = useRef(miscSettings);
    miscSettingsRef.current = miscSettings;
    const [subtitleSettings, setSubtitleSettings] = useState<SubtitleSettings>(settings);
    const [ankiSettings, setAnkiSettings] = useState<AnkiSettings>(settings);
    const playbackPreferences = usePlaybackPreferences();
    const [displaySubtitles, setDisplaySubtitles] = useState(playbackPreferences.displaySubtitles);
    const [disabledSubtitleTracks, setDisabledSubtitleTracks] = useState<{ [index: number]: boolean }>({});
    const disabledSubtitleTracksRef = useRef(disabledSubtitleTracks);
    disabledSubtitleTracksRef.current = disabledSubtitleTracks;
    useEffect(() => {
        asbTrace('playback/video-player', 'Subtitle display state', { displaySubtitles, disabledSubtitleTracks });
    }, [displaySubtitles, disabledSubtitleTracks]);
    const [playModes, setPlayModes] = useState<Set<PlayMode>>(() => new Set([PlayMode.normal]));
    const synchronizePlaybackModes = useCallback(
        (modes: ReadonlySet<PlayMode>) => {
            const synchronizedModes = new Set(modes);
            setPlayModes(synchronizedModes);
            playerChannel.playModes(synchronizedModes);
        },
        [playerChannel]
    );
    const [subtitlePlayerHidden, setSubtitlePlayerHidden] = useState<boolean>(false);
    const [appBarHidden, setAppBarHidden] = useState<boolean>(playbackPreferences.theaterMode);
    const [subtitleAlignments, setSubtitleAlignments] = useState<SubtitleAlignment[]>(
        allSubtitleAlignments(subtitleSettings)
    );
    const [, setBottomSubtitlePositionOffset] = useState<number>(subtitleSettings.subtitlePositionOffset);
    const [, setTopSubtitlePositionOffset] = useState<number>(subtitleSettings.topSubtitlePositionOffset);
    const showSubtitlesRef = useRef<IndexedSubtitleModel[]>([]);
    showSubtitlesRef.current = showSubtitles;
    const invisibleSubtitlesRef = useRef<IndexedSubtitleModel[]>([]);
    invisibleSubtitlesRef.current = invisibleSubtitles;
    const showingSubtitleIndexesRef = useRef<readonly number[]>([]);
    const invisibleSubtitleIndexesRef = useRef<readonly number[]>([]);
    const playbackStateChangedRef = useRef<(state: PlaybackState) => void>(() => {});
    const clock = useMemo<Clock>(() => new Clock(() => performance.now()), []);
    const mousePositionRef = useRef<Point | undefined>(undefined);
    const [showCursor, setShowCursor] = useState<boolean>(isMobile);
    const lastMouseMovementTimestamp = useRef<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [alert, setAlert] = useState<{ open: boolean; notifications: AlertNotification[] }>({
        open: false,
        notifications: [],
    });
    const [alertDisableAutoHide, setAlertDisableAutoHide] = useState<boolean>(false);
    const [pendingPlaybackPosition, setPendingPlaybackPosition] = useState<number>();
    const resumePlaybackSnackbar = useSnackbar({
        open: pendingPlaybackPosition !== undefined,
        onClose: () => playbackEngineRef.current?.dismissPlaybackPosition(),
    });
    const [lastMinedRecord, setLastMinedRecord] = useState<MinedRecord>();
    const [trackCount, setTrackCount] = useState<number>(0);
    const [, forceRender] = useState<any>();
    const [mineIntervalStartTimestamp, setMineIntervalStartTimestamp] = useState<number>();
    const [blurOverlayVisible, setBlurOverlayVisible] = useState<boolean>(false);
    const handleBlurOverlayToggle = useCallback(() => setBlurOverlayVisible((v) => !v), []);
    const mobileOverlayRef = useRef<HTMLDivElement>(null);
    const bottomSubtitleContainerRef = useRef<HTMLDivElement>(null);
    const domCacheRef = useRef<OffscreenDomCache | undefined>(undefined);
    const updateSubtitleDomCacheRef = useRef<((subtitles: readonly IndexedSubtitleModel[]) => void) | undefined>(
        undefined
    );
    const thumbnailsRef = useRef<Map<number, string>>(new Map()); // cache thumbnails, in intervals of 5s
    const isGeneratingRef = useRef(false); // avoid subsequent calls to generate thumbnail while generating one

    useEffect(() => {
        setMiscSettings(settings);
        setSubtitleSettings(settings);
        setAnkiSettings(settings);
    }, [settings]);

    useEffect(() => {
        setSubtitleAlignments(allSubtitleAlignments(subtitleSettings));
        setBottomSubtitlePositionOffset(subtitleSettings.subtitlePositionOffset);
        setTopSubtitlePositionOffset(subtitleSettings.topSubtitlePositionOffset);
    }, [subtitleSettings]);

    const keyBinder = useAppKeyBinder(miscSettings.keyBindSet, extension);

    useEffect(() => {
        if (i18n.language !== miscSettings.language) {
            void i18n.changeLanguage(miscSettings.language);
        }
    }, [miscSettings]);

    const updatePlayerState = useCallback(() => {
        const video = videoRef.current;

        if (!video) {
            return;
        }

        if (video.paused) {
            playerChannel.pause(false);
        } else {
            playerChannel.play(false);
        }

        playerChannel.playbackRate(video.playbackRate, false);
        playerChannel.currentTime(video.currentTime, false);
        forceRender({});

        if (!video.paused) {
            isPausedDueToHoverRef.current = false;
        }
    }, [playerChannel]);

    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const notifyPlaybackRate = useCallback(
        (options: ReturnType<PlaybackEngine<IndexedSubtitleModel>['playbackRateChanged']>) => {
            if (!options?.notify) return;
            setAlert({
                open: true,
                notifications: [
                    {
                        key: options.notification.key,
                        message: {
                            locKey: options.notification.locKey,
                            replacements: options.notification.replacements,
                        },
                        severity: 'info',
                    },
                ],
            });
        },
        []
    );

    const updatePlaybackRate = useCallback(
        (playbackRate: number, forwardToPlayer: boolean) => {
            if (forwardToPlayer) playerChannel.playbackRate(playbackRate);
            const playbackEngine = playbackEngineRef.current;
            if (!playbackEngine) return;

            const result = playbackEngine.playbackRateChanged(playbackRate);
            if (result === undefined) return;
            clock.rate = result.playbackRate;
            notifyPlaybackRate(result);
        },
        [clock, notifyPlaybackRate, playerChannel]
    );
    const synchronizePlaybackModesRef = useRef(synchronizePlaybackModes);
    synchronizePlaybackModesRef.current = synchronizePlaybackModes;

    const handlePlaybackModesChanged = useCallback(
        (transition: PlayModeTransition, options: PlaybackModeNotificationFormatOptions = {}): AlertNotification[] => {
            synchronizePlaybackModesRef.current(transition.modes);
            if (!transition.added.size && !transition.removed.size) return [];
            return formatPlaybackModeNotifications(transition, options).map(({ key, text }) => ({
                key,
                message: text,
                severity: 'info',
            }));
        },
        []
    );

    const handlePlaybackRateChanged = useCallback(
        (playbackRate: number) => updatePlaybackRate(playbackRate, false),
        [updatePlaybackRate]
    );
    const handleDurationChanged = useCallback(
        (durationMs: number) => {
            setLengthMs(durationMs);
            playerChannel.duration(durationMs / 1000);
            playbackEngineRef.current?.durationChanged(durationMs);
        },
        [playerChannel]
    );

    const videoRefCallback = useCallback(
        (element: HTMLVideoElement | null) => {
            if (!element) {
                asbTrace('playback/video-player', 'Detached video element');
                videoRef.current = undefined;
                setVideo(undefined);
                return;
            }
            if (element === videoRef.current) return;

            const videoElement = element as ExperimentalHTMLVideoElement;
            asbTrace('playback/video-player', 'Attached video element', {
                readyState: videoElement.readyState,
                currentTimeMs: videoElement.currentTime * 1000,
                paused: videoElement.paused,
                videoWidth: videoElement.videoWidth,
                videoHeight: videoElement.videoHeight,
            });
            videoRef.current = videoElement;
            setVideo(videoElement);
            clock.setTime(videoElement.currentTime * 1000, { paused: videoElement.paused });

            if (videoElement.readyState === 4) {
                notifyReady(videoElement, playerChannel, setAudioTracks, setSelectedAudioTrack);
                setVideoWidth(videoElement.videoWidth);
                setVideoHeight(videoElement.videoHeight);
            } else {
                videoElement.onloadeddata = () => {
                    notifyReady(videoElement, playerChannel, setAudioTracks, setSelectedAudioTrack);
                    setVideoWidth(videoElement.videoWidth);
                    setVideoHeight(videoElement.videoHeight);
                };
                videoElement.ondurationchange = () =>
                    notifyReady(videoElement, playerChannel, setAudioTracks, setSelectedAudioTrack);
            }

            videoElement.oncanplay = () => {
                asbTrace('playback/video-player', 'Video became playable', {
                    readyState: videoElement.readyState,
                    currentTimeMs: videoElement.currentTime * 1000,
                });
                playerChannel.readyState(4);
                clock.setTime(videoElement.currentTime * 1000, { paused: videoElement.paused });
            };

            if (isMobile) videoElement.volume = 1; // Force volume to 1 on mobile - users can control device volume
        },
        [clock, playerChannel]
    );

    const updateSubtitlesWithOffset = useCallback((offset: number, notifyPlayer: boolean, notificationKey: string) => {
        const previousOffset = offsetRef.current;
        offsetRef.current = offset;
        setOffset(offset);
        if (notifyPlayer) {
            setAlert({
                open: true,
                notifications: [{ key: notificationKey, message: formatAsSignedMs(offset), severity: 'info' }],
            });
        }
        if (offset === previousOffset) return;

        const shiftedSubtitles = subtitlesRef.current.map((s, i) => ({
            text: s.text,
            textImage: s.textImage,
            start: s.originalStart + offset,
            originalStart: s.originalStart,
            end: s.originalEnd + offset,
            originalEnd: s.originalEnd,
            track: s.track,
            index: i,
            tokenization: s.tokenization,
        }));
        subtitlesRef.current = shiftedSubtitles;
        setSubtitles(shiftedSubtitles);
    }, []);

    useEffect(() => {
        if (!video) return;

        asbTrace('playback/video-player', 'Creating video playback owner', {
            subtitleCount: subtitlesRef.current.length,
            hasVideoFileName: videoFileNameRef.current !== undefined,
            appIntegration: extension.supportsAppIntegration,
        });
        const playbackEngine = new PlaybackEngine({
            settingsProvider,
            appIntegration: extension.supportsAppIntegration,
            autoPauseCorrectionDisabled: false,
            subtitles: subtitlesRef.current,
            playbackModesDisabled: false,
            playbackModesSuppressed: false,
            playbackPositionKeys: videoFileNameRef.current ? [videoFileNameRef.current] : [],
            timingDriver: new VideoFrameTimingDriver(
                {
                    paused: () => video.paused,
                    playbackRate: () => video.playbackRate,
                    durationMs: () => video.duration * 1000,
                    currentTimeMs: () => video.currentTime * 1000,
                    hasVideoTrack: () =>
                        video.readyState >= HTMLMediaElement.HAVE_METADATA &&
                        video.videoWidth > 0 &&
                        video.videoHeight > 0,
                    frameTimestampMs: () => undefined,
                    externalSeekEvents: false,
                    requestVideoFrameCallback: (callback) => video.requestVideoFrameCallback(callback),
                    cancelVideoFrameCallback: (handle) => video.cancelVideoFrameCallback(handle),
                    addEventListener: (type, listener) => video.addEventListener(type, listener),
                    removeEventListener: (type, listener) => video.removeEventListener(type, listener),
                },
                {
                    onPlay: () => {
                        clock.start();
                        updatePlayerState();
                    },
                    onPause: () => {
                        clock.stop();
                        updatePlayerState();
                    },
                    onSeeked: (timestampMs) => {
                        clock.setTime(timestampMs, { paused: video.paused }); // rVFC may not run during pause
                        updatePlayerState();
                    },
                    onPlaybackRateChanged: handlePlaybackRateChanged,
                    onDurationChanged: handleDurationChanged,
                    onError: () => {
                        const error = errorMessageFromVideo(video);
                        asbTrace('playback/video-player', 'Observed video error', { error });
                        onErrorRef.current?.(error);
                    },
                }
            ),
            callbacks: {
                pause: () => {
                    video.pause();
                    clock.stop();
                },
                play: async () => {
                    await video.play();
                    clock.start();
                },
                seek: async (timestampMs) => {
                    video.currentTime = timestampMs / 1000;
                    clock.setTime(timestampMs, { paused: video.paused });
                },
                setPlaybackRate: (playbackRate) => {
                    if (video.playbackRate !== playbackRate) video.playbackRate = playbackRate;
                },
                setSubtitleOffset: (offset, options, notificationKey) =>
                    updateSubtitlesWithOffset(offset, options.notifyPlayer, notificationKey),
                playbackStateChanged: (state) => {
                    playbackStateChangedRef.current(state);
                    playerChannel.playbackState(state);
                },
                playbackPositionChanged: setPendingPlaybackPosition,
                saveSettings: (settings) => onSettingsChangedRef.current(settings),
                playbackModesChanged: (transition) => {
                    const notifications = handlePlaybackModesChanged(transition);
                    if (notifications.length) setAlert({ open: true, notifications });
                },
                initialPlaybackSettingsChanged: (settings) => {
                    asbTrace('playback/video-player', 'Applying initial video playback settings', {
                        subtitleOffset: settings.subtitleOffset,
                        playbackRate: settings.playbackRate,
                        playbackModes: [...settings.playbackModeTransition.modes],
                    });
                    playerChannel.offset(settings.subtitleOffset);
                    clock.rate = settings.playbackRate;
                    playerChannel.playbackRate(settings.playbackRate, false);

                    const offsetAndRate = (_t: TFunction) =>
                        settings.notifications.offsetAndRate
                            .map((notification) =>
                                notification.type === 'message'
                                    ? notification.message
                                    : _t(notification.notification.locKey, notification.notification.replacements)
                            )
                            .join(playbackModeNotificationJoin);
                    const playbackModeNotifications = handlePlaybackModesChanged(settings.playbackModeTransition, {
                        includeTransition: false,
                    });
                    const notifications: AlertNotification[] = [];
                    if (offsetAndRate.length) {
                        notifications.push({
                            message: offsetAndRate,
                            severity: 'info',
                            autoHideDuration: settings.autoHideDuration,
                        });
                    }
                    notifications.push(
                        ...playbackModeNotifications.map((notification) => ({
                            ...notification,
                            autoHideDuration: settings.autoHideDuration,
                        }))
                    );
                    if (notifications.length) setAlert({ open: true, notifications });
                },
                onError: (error) => {
                    asbTrace('playback/video-player', 'Video playback owner reported an error', { error });
                    onErrorRef.current?.(String(error));
                },
            },
        });

        playbackEngineRef.current = playbackEngine;
        asbTrace('playback/video-player', 'Binding video playback owner');
        playbackEngine.bind();

        return () => {
            asbTrace('playback/video-player', 'Unbinding video playback owner');
            playbackEngine.unbind();
            if (playbackEngineRef.current === playbackEngine) {
                playbackEngineRef.current = undefined;
            }
        };
    }, [
        clock,
        extension,
        handleDurationChanged,
        handlePlaybackRateChanged,
        handlePlaybackModesChanged,
        playerChannel,
        settingsProvider,
        updatePlayerState,
        updateSubtitlesWithOffset,
        video,
    ]);

    useEffect(() => {
        if (profileRef.current === profile) return;
        profileRef.current = profile;
        playbackEngineRef.current?.profileChanged(profile);
    }, [profile]);

    function selectAudioTrack(id: string) {
        const audioTracks = videoRef.current?.audioTracks;

        if (!audioTracks) {
            return;
        }

        for (const t of audioTracks) {
            if (t.id === id) {
                t.enabled = true;
            } else {
                t.enabled = false;
            }
        }
    }

    const togglePlaybackMode = useCallback(
        (targetMode: PlayMode) => playbackEngineRef.current?.togglePlaybackMode(targetMode),
        []
    );

    useEffect(() => {
        const playbackEngine = playbackEngineRef.current;
        if (!playerChannelSubscribed || !playbackEngine) return;
        playbackEngine.settingsChanged({ ...settings, ...miscSettings });
    }, [miscSettings, playerChannelSubscribed, settings, video]);

    useEffect(() => {
        const playbackEngine = playbackEngineRef.current;
        if (!playerChannelSubscribed || !playbackEngine) return;
        playbackEngine.subtitlesChanged(subtitles);
    }, [playerChannelSubscribed, subtitles, video]);

    useEffect(() => {
        asbTrace('playback/video-player', 'Subscribing to player channel');
        playerChannel.onReady((duration, videoFileName) => {
            asbTrace('playback/video-player', 'Received player-channel ready event', {
                durationMs: duration * 1000,
                hasVideoFileName: videoFileName !== undefined,
            });
            setLengthMs(duration);
            setVideoFileName(videoFileName);
            videoFileNameRef.current = videoFileName;
            playbackEngineRef.current?.playbackPositionKeysChanged(videoFileName ? [videoFileName] : [], {
                isNewSource: true,
            });
        });

        playerChannel.onPlay(() => {
            void (async () => {
                await videoRef.current?.play();
                clock.start();
            })();
        });

        playerChannel.onPause(() => {
            videoRef.current?.pause();
            clock.stop();
        });

        playerChannel.onCurrentTime((currentTime) => {
            let actualCurrentTime = currentTime;

            if (videoRef.current) {
                actualCurrentTime = seekWithNudge(videoRef.current, currentTime);
            }

            if (videoRef.current?.readyState === 4) {
                playerChannel.readyState(4);
            }

            clock.setTime(actualCurrentTime * 1000, { paused: true });
        });

        playerChannel.onAudioTrackSelected((id) => {
            selectAudioTrack(id);
            setSelectedAudioTrack(id);
            playerChannel.audioTrackSelected(id);
        });

        playerChannel.onClose(() => {
            asbTrace('playback/video-player', 'Received player-channel close command');
            playerChannel.close();
            window.close();
        });

        playerChannel.onSubtitles((subtitles) => {
            asbTrace('playback/video-player', 'Received subtitles from player channel', {
                subtitleCount: subtitles.length,
            });
            const offset = playbackEngineRef.current?.lastSubtitleOffset ?? 0;
            const videoSubtitles = subtitles.map((s, i) => ({
                ...s,
                start: s.originalStart + offset,
                end: s.originalEnd + offset,
                index: i,
            }));
            subtitlesRef.current = videoSubtitles;
            setSubtitles(videoSubtitles);
            setTrackCount(Math.max(...videoSubtitles.map((s) => s.track)) + 1);

            if (videoSubtitles.length > 0) {
                offsetRef.current = offset;
                setOffset(offset);
            }

            playerChannel.offset(offset);

            setShowSubtitles([]);
            setInvisibleSubtitles([]);
        });
        playerChannel.onSubtitlesUpdated((updatedSubtitles) => {
            asbTrace('playback/video-player', 'Received subtitle update from player channel', {
                subtitleCount: updatedSubtitles.length,
            });
            updateSubtitleDomCacheRef.current?.(updatedSubtitles);

            const updatedByIndex = new Map(updatedSubtitles.map((s) => [s.index, s] as const));
            if (showSubtitlesRef.current.some((s) => updatedByIndex.has(s.index))) {
                setShowSubtitles((prevShowSubtitles) =>
                    prevShowSubtitles.map((showSubtitle) => {
                        const updatedShowSubtitle = updatedByIndex.get(showSubtitle.index);
                        if (!updatedShowSubtitle) return showSubtitle;
                        return {
                            ...showSubtitle,
                            text: updatedShowSubtitle.text,
                            tokenization: updatedShowSubtitle.tokenization,
                        };
                    })
                );
            }

            if (!subtitlesRef.current.length) return;
            const allSubtitles = subtitlesRef.current.slice();
            for (const s of updatedSubtitles) {
                allSubtitles[s.index] = {
                    ...allSubtitles[s.index],
                    text: s.text,
                    tokenization: s.tokenization,
                };
            }
            subtitlesRef.current = allSubtitles;
            setSubtitles(allSubtitles);
        });

        playerChannel.onPlayMode((targetMode) => togglePlaybackMode(targetMode));
        playerChannel.onHideSubtitlePlayerToggle((hidden) => setSubtitlePlayerHidden(hidden));
        playerChannel.onAppBarToggle((hidden) => setAppBarHidden(hidden));
        playerChannel.onFullscreenToggle((fullscreen) => requestFullscreen(fullscreen));
        playerChannel.onSubtitleSettings(setSubtitleSettings);
        playerChannel.onMiscSettings(setMiscSettings);
        playerChannel.onAnkiSettings(setAnkiSettings);
        playerChannel.onOffset((offset) => {
            const playbackEngine = playbackEngineRef.current;
            if (!playbackEngine) return;
            playbackEngine.subtitleOffsetChanged(offset, { notifyPlayer: true });
        });
        playerChannel.onPlaybackRate((playbackRate) => updatePlaybackRate(playbackRate, false));
        playerChannel.onAlert((message, severity) => {
            setAlert({
                open: true,
                notifications: [{ message, severity: severity as AlertNotification['severity'] }],
            });
        });

        window.onbeforeunload = () => {
            if (!poppingInRef.current) {
                playerChannel.close();
            }
        };

        setPlayerChannelSubscribed(true);
        playerChannel.playModes(playbackEngineRef.current?.playbackModes ?? new Set([PlayMode.normal]));
        asbTrace('playback/video-player', 'Player channel subscribed');
        return () => {
            asbTrace('playback/video-player', 'Closing player channel');
            playerChannel.close();
        };
    }, [clock, playerChannel, requestFullscreen, togglePlaybackMode, updatePlaybackRate, updateSubtitlesWithOffset]);

    const handlePlay = useCallback(() => {
        if (videoRef.current) {
            playerChannel.play();
        }
    }, [playerChannel]);

    const handlePause = useCallback(() => playerChannel.pause(), [playerChannel]);

    const handleSeek = useCallback(
        (progress: number) => {
            if (!Number.isFinite(lengthMs)) {
                return;
            }

            if (playing()) {
                clock.stop();
            }

            const time = progress * lengthMs;
            // get a screenshot of this time when hovered
            playerChannel.currentTime(time / 1000);
        },
        [lengthMs, clock, playerChannel]
    );

    const handleSeekPreview = useCallback(
        (progress: number): string | undefined => {
            if (!Number.isFinite(lengthMs)) {
                return;
            }

            if (!hiddenVideoRef.current) {
                return;
            }

            const time = progress * lengthMs;
            const video = hiddenVideoRef.current;

            const thumbnailKey = Math.floor(time / 1000 / 5);

            // cached url found, return url
            if (thumbnailsRef.current.has(thumbnailKey)) {
                return thumbnailsRef.current.get(thumbnailKey);
            }

            if (isGeneratingRef.current) return;
            // if not in cache, generate new one in the background
            isGeneratingRef.current = true;

            const onSeeked = () => {
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth * 0.7;
                canvas.height = video.videoHeight * 0.7;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

                const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
                thumbnailsRef.current.set(thumbnailKey, thumbnailUrl);
                isGeneratingRef.current = false;
            };

            video.addEventListener('seeked', onSeeked, { once: true });
            video.currentTime = time / 1000;

            return;
        },
        [lengthMs]
    );

    // load or unload preview thumbnail
    useEffect(() => {
        const videoPreview = hiddenVideoRef.current;

        if (!videoPreview) return;

        if (settings.thumbnailPreview) {
            videoPreview.src = videoFile;
            videoPreview.load();
        } else {
            videoPreview.pause();
            videoPreview.removeAttribute('src');
            videoPreview.load();
        }
    }, [settings.thumbnailPreview, videoFile, hiddenVideoReady]);

    const handleSeekByTimestamp = useCallback(
        (timestampMs: number) => {
            playerChannel.currentTime(timestampMs / 1000);
        },
        [playerChannel]
    );

    const tokenSelectionCurrentTime = useCallback(() => clock.time({ maxMs: lengthMs }), [clock, lengthMs]);
    const tokenSelectionSeekableTracks = useCallback(() => miscSettings.seekableTracks, [miscSettings.seekableTracks]);
    const tokenSelectionDisabled = useCallback(() => false, []);
    const handleTokenSelectionMatch = useCallback(
        (match: TokenJumpMatch) => {
            handleSeekByTimestamp(match.subtitle.start);
        },
        [handleSeekByTimestamp]
    );

    useTokenSelection({
        rootRef: containerRef,
        maxAttempts: 60,
        keyBinder,
        subtitles,
        getCurrentTime: tokenSelectionCurrentTime,
        getSeekableTracks: tokenSelectionSeekableTracks,
        onMatch: handleTokenSelectionMatch,
        onTokenSelectionClaimed: playerChannel.tokenSelectionFocus,
        disabledGetter: tokenSelectionDisabled,
    });

    useEffect(() => {
        if (seekRequest !== undefined) {
            handleSeek(seekRequest.timestamp / lengthMs);
        }
    }, [handleSeek, seekRequest, lengthMs]);

    const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        lastMouseMovementTimestamp.current = Date.now();

        if (!containerRef.current) {
            return;
        }

        const bounds = containerRef.current.getBoundingClientRect();
        mousePositionRef.current = { x: e.clientX - bounds.left, y: e.clientY - bounds.top };
    }, []);

    const handleMouseLeave = useCallback(() => {
        mousePositionRef.current = undefined;
    }, []);

    const handleAudioTrackSelected = useCallback(
        (id: string) => {
            if (playing()) {
                clock.stop();
                playerChannel.pause();
            }

            selectAudioTrack(id);
            setSelectedAudioTrack(id);
            playerChannel.currentTime(0);
            playerChannel.audioTrackSelected(id);
        },
        [playerChannel, clock]
    );

    const handleLoadFiles = useCallback(() => {
        playerChannel.loadFiles();
    }, [playerChannel]);

    const handleLoadSubtitles = useCallback(() => {
        playerChannel.loadSubtitles();
    }, [playerChannel]);

    const updateShowingSubtitles = useCallback(
        (showingSubtitleIndexes: readonly number[], invisibleSubtitleIndexes: readonly number[]) => {
            const subtitlesForIndexes = (indexes: readonly number[]) =>
                indexes
                    .map((index) => subtitlesRef.current[index])
                    .filter(
                        (subtitle): subtitle is IndexedSubtitleModel =>
                            subtitle !== undefined && !disabledSubtitleTracksRef.current[subtitle.track]
                    )
                    .slice()
                    .sort(compareSubtitlesForDisplay);
            const showingSubtitles = subtitlesForIndexes(showingSubtitleIndexes);
            const nextInvisibleSubtitles = subtitlesForIndexes(invisibleSubtitleIndexes);
            const showingChanged = !arrayEquals(showingSubtitles, showSubtitlesRef.current);
            const invisibleChanged = !arrayEquals(nextInvisibleSubtitles, invisibleSubtitlesRef.current);
            if (!showingChanged && !invisibleChanged) return;

            if (showingChanged) {
                showSubtitlesRef.current = showingSubtitles;
                setShowSubtitles(showingSubtitles);
            }
            if (invisibleChanged) {
                invisibleSubtitlesRef.current = nextInvisibleSubtitles;
                setInvisibleSubtitles(nextInvisibleSubtitles);
            }
            if (
                showingChanged &&
                showingSubtitles.length > 0 &&
                miscSettingsRef.current.autoCopyCurrentSubtitle &&
                document.hasFocus()
            ) {
                navigator.clipboard
                    .writeText(showingSubtitles.map((subtitle) => subtitle.text).join('\n'))
                    .catch(() => {
                        // ignore
                    });
            }
        },
        []
    );

    playbackStateChangedRef.current = (state) => {
        const hiddenSubtitleIndexes = state.hiddenSubtitleIndexes ?? [];
        const visibleSubtitleIndexes = state.showingSubtitleIndexes.filter(
            (index) => !hiddenSubtitleIndexes.includes(index)
        );
        showingSubtitleIndexesRef.current = visibleSubtitleIndexes;
        const invisibleSubtitleIndexes = (state.invisibleSubtitleIndexes ?? []).filter(
            (index) => !hiddenSubtitleIndexes.includes(index)
        );
        invisibleSubtitleIndexesRef.current = invisibleSubtitleIndexes;
        updateShowingSubtitles(visibleSubtitleIndexes, invisibleSubtitleIndexes);
    };

    useEffect(() => {
        updateShowingSubtitles(showingSubtitleIndexesRef.current, invisibleSubtitleIndexesRef.current);
    }, [disabledSubtitleTracks, updateShowingSubtitles]);

    const handleOffsetChange = useCallback(
        (offset: number) => {
            playbackEngineRef.current?.subtitleOffsetChanged(offset, { notifyPlayer: true });
            playerChannel.offset(offset);
        },
        [playerChannel]
    );

    const handlePlaybackRateChange = useCallback(
        (playbackRate: number) => {
            updatePlaybackRate(playbackRate, true);
        },
        [updatePlaybackRate]
    );

    useEffect(() => {
        return keyBinder.bindSeekToSubtitle(
            (event, subtitle) => {
                event.preventDefault();
                playerChannel.currentTime(subtitle.start / 1000);
            },
            () => !videoRef.current,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => settings.seekableTracks
        );
    }, [keyBinder, playerChannel, subtitles, lengthMs, clock, settings]);

    useEffect(() => {
        return keyBinder.bindSeekToBeginningOfCurrentSubtitle(
            (event, subtitle) => {
                event.preventDefault();
                playerChannel.currentTime(subtitle.start / 1000);

                if (settings.alwaysPlayOnSubtitleRepeat) {
                    playerChannel.play();
                }
            },
            () => !videoRef.current,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => settings.seekableTracks
        );
    }, [keyBinder, playerChannel, subtitles, lengthMs, clock, settings]);

    useEffect(() => {
        return keyBinder.bindSeekBackwardOrForward(
            (event, forward) => {
                event.preventDefault();
                const timestamp = clock.time({ maxMs: lengthMs });
                const seekDuration = miscSettings.seekDuration * 1000;

                if (forward) {
                    playerChannel.currentTime(Math.min(lengthMs / 1000, (timestamp + seekDuration) / 1000));
                } else {
                    playerChannel.currentTime(Math.max(0, (timestamp - seekDuration) / 1000));
                }
            },
            () => !videoRef.current
        );
    }, [keyBinder, playerChannel, lengthMs, clock, miscSettings]);

    const calculateSurroundingSubtitles = useCallback(
        (index: number) => {
            return surroundingSubtitles(
                subtitles,
                index,
                ankiSettings.surroundingSubtitlesCountRadius,
                ankiSettings.surroundingSubtitlesTimeRadius
            );
        },
        [subtitles, ankiSettings.surroundingSubtitlesCountRadius, ankiSettings.surroundingSubtitlesTimeRadius]
    );

    useEffect(() => {
        return keyBinder.bindAdjustOffset(
            (event, offset) => {
                event.preventDefault();
                handleOffsetChange(offset);
            },
            () => false,
            () => subtitles
        );
    }, [keyBinder, handleOffsetChange, subtitles]);

    useEffect(() => {
        return keyBinder.bindResetOffet(
            (event) => {
                event.preventDefault();
                handleOffsetChange(0);
            },
            () => false
        );
    }, [keyBinder, handleOffsetChange]);

    useEffect(() => {
        return keyBinder.bindAdjustPlaybackRate(
            (event, increase) => {
                const playbackEngine = playbackEngineRef.current;
                if (!playbackEngine) return;
                event.preventDefault();

                notifyPlaybackRate(
                    playbackEngine.adjustPlaybackRate(
                        increase ? miscSettings.speedChangeStep : -miscSettings.speedChangeStep
                    )
                );
            },
            () => false
        );
    }, [updatePlaybackRate, keyBinder, miscSettings, notifyPlaybackRate]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitles(
            (event) => {
                event.preventDefault();
                setDisplaySubtitles(!displaySubtitles);
                playbackPreferences.displaySubtitles = !displaySubtitles;
            },
            () => false
        );
    }, [keyBinder, displaySubtitles, playbackPreferences]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleTrackInVideo(
            (event, track) => {
                event.preventDefault();
                setDisabledSubtitleTracks((tracks) => {
                    const newTracks = { ...tracks };
                    newTracks[track] = !tracks[track];
                    return newTracks;
                });
            },
            () => false
        );
    }, [keyBinder]);

    useEffect(() => {
        return keyBinder.bindAdjustSubtitlePositionOffset(
            (event, increase) => {
                const newSubtitleSettings = { ...subtitleSettings };

                event.preventDefault();
                if (increase) {
                    newSubtitleSettings.subtitlePositionOffset = subtitleSettings.subtitlePositionOffset + 20;
                } else {
                    newSubtitleSettings.subtitlePositionOffset = subtitleSettings.subtitlePositionOffset - 20;
                }

                onSettingsChanged(newSubtitleSettings);
                setSubtitleSettings(newSubtitleSettings);
            },
            () => false
        );
    }, [keyBinder, subtitleSettings, onSettingsChanged]);

    useEffect(() => {
        return keyBinder.bindAdjustTopSubtitlePositionOffset(
            (event, increase) => {
                const newSubtitleSettings = { ...subtitleSettings };

                event.preventDefault();
                if (increase) {
                    newSubtitleSettings.topSubtitlePositionOffset = subtitleSettings.topSubtitlePositionOffset + 20;
                } else {
                    newSubtitleSettings.topSubtitlePositionOffset = subtitleSettings.topSubtitlePositionOffset - 20;
                }

                onSettingsChanged(newSubtitleSettings);
                setSubtitleSettings(newSubtitleSettings);
            },
            () => false
        );
    }, [keyBinder, subtitleSettings, onSettingsChanged]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleTrackInList(
            (event, track) => {
                event.preventDefault();
                playerChannel.toggleSubtitleTrackInList(track);
            },
            () => false
        );
    }, [keyBinder, playerChannel]);

    useEffect(() => {
        return keyBinder.bindUnblurTrack(
            (event, targetTrack) => {
                event.preventDefault();
                let newSubtitleSettings = { ...subtitleSettings };

                for (let currentTrack = 0; currentTrack < trackCount; ++currentTrack) {
                    const originalValue = textSubtitleSettingsForTrack(subtitleSettings, currentTrack).subtitleBlur!;
                    const targetValue = currentTrack === targetTrack ? !originalValue : originalValue;
                    const change = changeForTextSubtitleSetting(
                        { subtitleBlur: targetValue },
                        newSubtitleSettings,
                        currentTrack
                    );
                    newSubtitleSettings = { ...newSubtitleSettings, ...change };
                }

                onSettingsChanged(newSubtitleSettings);
                setSubtitleSettings(newSubtitleSettings);
            },
            () => false
        );
    }, [keyBinder, subtitleSettings, trackCount, onSettingsChanged]);

    useEffect(() => {
        return keyBinder.bindOffsetToSubtitle(
            (event, offset) => {
                event.preventDefault();
                handleOffsetChange(offset);
            },
            () => false,
            () => clock.time({ maxMs: lengthMs }),
            () => subtitles,
            () => miscSettings.seekableTracks
        );
    }, [keyBinder, handleOffsetChange, subtitles, clock, lengthMs, miscSettings.seekableTracks]);

    const extractSubtitles = useCallback(() => {
        if (!subtitles || subtitles.length === 0) {
            const timestamp = clock.time({ maxMs: lengthMs });
            const end = Math.min(timestamp + 5000, lengthMs);
            const currentSubtitle = {
                text: '',
                start: timestamp,
                originalStart: timestamp,
                end: end,
                originalEnd: end,
                track: 0,
            };

            return { currentSubtitle, surroundingSubtitles: mockSurroundingSubtitles(currentSubtitle, lengthMs, 5000) };
        } else if (showSubtitlesRef.current && showSubtitlesRef.current.length > 0) {
            const currentSubtitle = showSubtitlesRef.current[0];
            return { currentSubtitle, surroundingSubtitles: calculateSurroundingSubtitles(currentSubtitle.index) };
        }

        return undefined;
    }, [subtitles, calculateSurroundingSubtitles, lengthMs, clock]);

    const mineSubtitle = useCallback(
        (
            postMineAction: PostMineAction,
            videoFileUrl: string,
            videoFileName: string,
            selectedAudioTrack: string | undefined,
            playbackRate: number,
            subtitle: SubtitleModel,
            surroundingSubtitles: SubtitleModel[],
            cardTextFieldValues: CardTextFieldValues,
            timestamp: number
        ) => {
            switch (postMineAction) {
                case PostMineAction.showAnkiDialog:
                    playerChannel.copy(
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        videoFileName ?? '',
                        timestamp,
                        PostMineAction.none
                    );
                    onAnkiDialogRequest(
                        videoFileUrl,
                        videoFileName ?? '',
                        selectedAudioTrack,
                        playbackRate,
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        timestamp
                    );

                    if (playing()) {
                        playerChannel.pause();
                        setWasPlayingOnAnkiDialogRequest(true);
                    } else {
                        setWasPlayingOnAnkiDialogRequest(false);
                    }
                    break;
                default:
                    playerChannel.copy(
                        subtitle,
                        surroundingSubtitles,
                        cardTextFieldValues,
                        videoFileName ?? '',
                        timestamp,
                        postMineAction
                    );
            }

            setLastMinedRecord({
                videoFileUrl,
                videoFileName: videoFileName ?? '',
                selectedAudioTrack,
                playbackRate,
                subtitle,
                surroundingSubtitles,
                timestamp,
            });
        },
        [onAnkiDialogRequest, playerChannel]
    );

    const mineCurrentSubtitle = useCallback(
        (
            postMineAction: PostMineAction,
            subtitle?: SubtitleModel,
            surroundingSubtitles?: SubtitleModel[],
            cardTextFieldValues?: CardTextFieldValues
        ) => {
            const video = videoRef.current;

            if (!video) {
                return;
            }

            const currentTimestamp = clock.time({ maxMs: lengthMs });
            let mediaTimestamp: number;

            if (subtitle === undefined || surroundingSubtitles === undefined) {
                const extracted = extractSubtitles();

                if (extracted === undefined) {
                    return;
                }

                subtitle = extracted.currentSubtitle;
                surroundingSubtitles = extracted.surroundingSubtitles;
                mediaTimestamp = currentTimestamp;
            } else if (currentTimestamp >= subtitle.start && currentTimestamp <= subtitle.end) {
                mediaTimestamp = currentTimestamp;
            } else {
                mediaTimestamp = subtitleTimestampWithDelay(subtitle, settings.streamingScreenshotDelay);
            }

            mineSubtitle(
                postMineAction,
                videoFile,
                videoFileName ?? '',
                selectedAudioTrack,
                video.playbackRate,
                subtitle,
                surroundingSubtitles,
                cardTextFieldValues ?? {},
                mediaTimestamp
            );
        },
        [
            mineSubtitle,
            extractSubtitles,
            clock,
            settings.streamingScreenshotDelay,
            selectedAudioTrack,
            videoFile,
            videoFileName,
            lengthMs,
        ]
    );

    const toggleSelectMiningInterval = useCallback(
        (postMineAction: PostMineAction, cardTextFieldValues?: CardTextFieldValues) => {
            if (mineIntervalStartTimestamp === undefined) {
                setMineIntervalStartTimestamp(clock.time({ maxMs: lengthMs }));

                if (!playing()) {
                    playerChannel.play();
                }

                if (!isMobile) {
                    setAlert({
                        open: true,
                        notifications: [
                            {
                                message: { locKey: 'info.manualMiningIntervalPrompt', replacements: {} },
                                severity: 'info',
                            },
                        ],
                    });
                    setAlertDisableAutoHide(true);
                }
            } else {
                setAlertDisableAutoHide(false);
                setAlert((alert) => ({ ...alert, open: false }));
                const video = videoRef.current;

                if (!video) {
                    return;
                }

                const endTimestamp = clock.time({ maxMs: lengthMs });

                if (endTimestamp > mineIntervalStartTimestamp) {
                    let currentSubtitle: SubtitleModel = {
                        text: '',
                        start: mineIntervalStartTimestamp,
                        originalStart: mineIntervalStartTimestamp,
                        end: endTimestamp,
                        originalEnd: endTimestamp,
                        track: 0,
                    };
                    let surroundingSubtitles: SubtitleModel[];

                    if (subtitles.length === 0) {
                        surroundingSubtitles = mockSurroundingSubtitles(currentSubtitle, lengthMs, 5000);
                    } else {
                        const calculated = surroundingSubtitlesAroundInterval(
                            subtitles,
                            mineIntervalStartTimestamp,
                            endTimestamp,
                            settings.surroundingSubtitlesCountRadius,
                            settings.surroundingSubtitlesTimeRadius
                        );
                        currentSubtitle = {
                            ...currentSubtitle,
                            text: calculated.subtitle?.text ?? '',
                        };
                        surroundingSubtitles = calculated.surroundingSubtitles ?? [];
                    }

                    mineSubtitle(
                        postMineAction,
                        videoFile,
                        videoFileName ?? '',
                        selectedAudioTrack,
                        video.playbackRate,
                        currentSubtitle,
                        surroundingSubtitles,
                        cardTextFieldValues ?? {},
                        mineIntervalStartTimestamp
                    );
                }

                setMineIntervalStartTimestamp(undefined);
            }
        },
        [
            mineSubtitle,
            playerChannel,
            mineIntervalStartTimestamp,
            clock,
            lengthMs,
            selectedAudioTrack,
            videoFile,
            videoFileName,
            subtitles,
            settings.surroundingSubtitlesCountRadius,
            settings.surroundingSubtitlesTimeRadius,
        ]
    );

    const inferAndExecuteMiningBehavior = useCallback(
        (
            postMineAction: PostMineAction,
            subtitle?: SubtitleModel,
            surroundingSubtitles?: SubtitleModel[],
            cardTextFieldValues?: CardTextFieldValues
        ) => {
            if (!subtitle && !surroundingSubtitles && subtitles.length === 0) {
                toggleSelectMiningInterval(postMineAction, cardTextFieldValues);
            } else {
                if (mineIntervalStartTimestamp !== undefined) {
                    // Edge case: user started manually recording but are now using an "automatic" mining shortcut
                    // Cancel the "recording" operation
                    setAlertDisableAutoHide(false);
                    setAlert((alert) => ({ ...alert, open: false }));
                    setMineIntervalStartTimestamp(undefined);
                }

                mineCurrentSubtitle(postMineAction, subtitle, surroundingSubtitles, cardTextFieldValues);
            }
        },
        [mineCurrentSubtitle, toggleSelectMiningInterval, mineIntervalStartTimestamp, subtitles]
    );

    useEffect(() => {
        return playerChannel.onCopy(inferAndExecuteMiningBehavior);
    }, [playerChannel, inferAndExecuteMiningBehavior]);

    useEffect(() => {
        return keyBinder.bindAnkiExport(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                inferAndExecuteMiningBehavior(PostMineAction.showAnkiDialog);
            },
            () => false
        );
    }, [inferAndExecuteMiningBehavior, keyBinder]);

    useEffect(() => {
        return miningContext.onEvent('stopped-mining', () => {
            switch (miscSettings.postMiningPlaybackState) {
                case PostMinePlayback.play:
                    playerChannel.play();
                    break;
                case PostMinePlayback.pause:
                    playerChannel.pause();
                    break;
                case PostMinePlayback.remember:
                    if (wasPlayingOnAnkiDialogRequest) {
                        playerChannel.play();
                    }
                    break;
            }
        });
    }, [miningContext, wasPlayingOnAnkiDialogRequest, miscSettings, playerChannel]);

    useEffect(() => {
        return keyBinder.bindUpdateLastCard(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                inferAndExecuteMiningBehavior(PostMineAction.updateLastCard);
            },
            () => false
        );
    }, [inferAndExecuteMiningBehavior, keyBinder]);

    useEffect(() => {
        return keyBinder.bindExportCard(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                inferAndExecuteMiningBehavior(PostMineAction.exportCard);
            },
            () => false
        );
    }, [inferAndExecuteMiningBehavior, keyBinder]);

    useEffect(() => {
        return keyBinder.bindTakeScreenshot(
            (event) => {
                event.preventDefault();

                if (ankiDialogOpen) {
                    onAnkiDialogRewind();
                } else if (lastMinedRecord) {
                    const currentTimestamp = clock.time({ maxMs: lengthMs });
                    mineSubtitle(
                        PostMineAction.showAnkiDialog,
                        lastMinedRecord.videoFileUrl,
                        lastMinedRecord.videoFileName,
                        lastMinedRecord.selectedAudioTrack,
                        lastMinedRecord.playbackRate,
                        lastMinedRecord.subtitle,
                        lastMinedRecord.surroundingSubtitles,
                        {},
                        currentTimestamp
                    );
                }
            },
            () => false
        );
    }, [clock, lengthMs, keyBinder, lastMinedRecord, mineSubtitle, popOut, ankiDialogOpen, onAnkiDialogRewind]);

    useEffect(() => {
        return keyBinder.bindToggleRecording(
            (event) => {
                event.preventDefault();
                toggleSelectMiningInterval(PostMineAction.showAnkiDialog);
            },
            () => false
        );
    }, [keyBinder, toggleSelectMiningInterval]);

    useEffect(() => {
        return keyBinder.bindCopy(
            (event) => {
                event.preventDefault();
                inferAndExecuteMiningBehavior(PostMineAction.none);
            },
            () => false,
            () => {
                const extracted = extractSubtitles();

                if (extracted === undefined) {
                    return undefined;
                }

                return extracted.currentSubtitle;
            }
        );
    }, [extractSubtitles, inferAndExecuteMiningBehavior, keyBinder]);

    useEffect(() => {
        return keyBinder.bindPlay(
            (event) => {
                event.preventDefault();

                if (playing()) {
                    playerChannel.pause();
                } else {
                    playerChannel.play();
                }
            },
            () => false
        );
    }, [keyBinder, playerChannel]);

    useEffect(() => {
        return keyBinder.bindAutoPause(
            (event) => {
                event.preventDefault();
                togglePlaybackMode(PlayMode.autoPause);
            },
            () => false
        );
    }, [keyBinder, togglePlaybackMode]);

    useEffect(() => {
        return keyBinder.bindCondensedPlayback(
            (event) => {
                event.preventDefault();
                togglePlaybackMode(PlayMode.condensed);
            },
            () => false
        );
    }, [keyBinder, togglePlaybackMode]);

    useEffect(() => {
        return keyBinder.bindFastForwardPlayback(
            (event) => {
                event.preventDefault();
                togglePlaybackMode(PlayMode.fastForward);
            },
            () => false
        );
    }, [keyBinder, togglePlaybackMode]);

    useEffect(() => {
        return keyBinder.bindToggleRepeat(
            (event) => {
                event.preventDefault();
                togglePlaybackMode(PlayMode.repeat);
            },
            () => false
        );
    }, [keyBinder, togglePlaybackMode]);

    useEffect(() => {
        return keyBinder.bindCycleAutoPauseResumeMode(
            (event) => {
                event.preventDefault();
                const notification = playbackEngineRef.current?.cycleAutoPauseResumeMode();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => subtitles.length === 0
        );
    }, [keyBinder, subtitles.length, t]);

    useEffect(() => {
        return keyBinder.bindToggleSubtitleVisibility(
            (event) => {
                event.preventDefault();
                const notification = playbackEngineRef.current?.toggleSubtitleVisibility();
                if (notification === undefined) return;
                setAlert({
                    open: true,
                    notifications: [
                        {
                            key: notification.key,
                            message: {
                                locKey: notification.locKey,
                                replacements: { value: t(notification.valueLocKey) },
                            },
                            severity: 'info',
                        },
                    ],
                });
            },
            () => subtitles.length === 0
        );
    }, [keyBinder, subtitles.length, t]);

    const handleSubtitlesToggle = useCallback(() => {
        setDisplaySubtitles(!displaySubtitles);
        playbackPreferences.displaySubtitles = !displaySubtitles;
    }, [displaySubtitles, playbackPreferences]);

    const handleFullscreenToggle = useCallback(() => {
        requestFullscreen(!fullscreen);
    }, [fullscreen, requestFullscreen]);

    const handleVolumeChange = useCallback((volume: number) => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }
    }, []);

    const handlePopOutToggle = useCallback(() => {
        playerChannel.popOutToggle();
        if (popOut) {
            poppingInRef.current = true;
            window.close();
        }
    }, [playerChannel, popOut]);

    const handlePlayMode = useCallback((targetMode: PlayMode) => togglePlaybackMode(targetMode), [togglePlaybackMode]);

    const handleClose = useCallback(() => {
        playerChannel.close();
        window.close();
    }, [playerChannel]);

    const handleHideSubtitlePlayerToggle = useCallback(() => {
        playerChannel.hideSubtitlePlayerToggle();
    }, [playerChannel]);

    const handleTheaterModeToggle = useCallback(() => {
        playerChannel.appBarToggle();
    }, [playerChannel]);

    const handleSubtitleAlignment = useCallback(
        (alignment: SubtitleAlignment) => {
            let change: Partial<SubtitleSettings> = {};

            for (let track = 0; track < subtitleAlignments.length; ++track) {
                change = {
                    ...change,
                    ...changeForTextSubtitleSetting({ subtitleAlignment: alignment }, subtitleSettings, track),
                };
            }

            const newSubtitleSettings = { ...subtitleSettings, ...change };
            setSubtitleAlignments([alignment]);
            onSettingsChanged(newSubtitleSettings);
            setSubtitleSettings(newSubtitleSettings);
        },
        [onSettingsChanged, subtitleSettings, subtitleAlignments]
    );

    const handleClick = useCallback(() => {
        if (playing()) {
            playerChannel.pause();
        } else {
            playerChannel.play();
        }
    }, [playerChannel]);

    const handleDoubleClick = useCallback(() => handleFullscreenToggle(), [handleFullscreenToggle]);

    useEffect(() => {
        if (isMobile) {
            return;
        }

        const interval = setInterval(() => {
            if (Date.now() - lastMouseMovementTimestamp.current > 300) {
                if (showCursor) {
                    setShowCursor(false);
                }
            } else if (!showCursor) {
                setShowCursor(true);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [showCursor]);

    const handleAlertClosed = useCallback(() => {
        setAlertDisableAutoHide(false);
        setAlert((alert) => ({ ...alert, open: false }));
    }, []);
    const renderDictionaryTracks = useStableDictionaryTracks(settings.dictionaryTracks);
    const trackStyles = useSubtitleStyles(subtitleSettings, trackCount ?? 1, renderDictionaryTracks, 'video');

    const getSubtitleHtml = useCallback(
        (subtitle: IndexedSubtitleModel) =>
            showingSubtitleHtml(
                subtitle,
                videoRef,
                trackStyles[subtitle.track]?.styleString ?? trackStyles[0]?.styleString ?? '',
                trackStyles[subtitle.track]?.classes ?? trackStyles[0]?.classes ?? '',
                subtitleSettings.imageBasedSubtitleScaleFactor,
                renderDictionaryTracks
            ),
        [trackStyles, renderDictionaryTracks, subtitleSettings.imageBasedSubtitleScaleFactor]
    );

    const { getSubtitleDomCache, updateSubtitleDomCache } = useSubtitleDomCache(subtitles, getSubtitleHtml);

    domCacheRef.current = getSubtitleDomCache();
    updateSubtitleDomCacheRef.current = updateSubtitleDomCache;

    const handleSwipe = useCallback(
        (direction: Direction) => {
            const subtitle = adjacentSubtitle(
                direction === 'right',
                clock.time({ maxMs: lengthMs }),
                subtitles,
                miscSettings.seekableTracks
            );
            if (subtitle) {
                playerChannel.currentTime(subtitle.start / 1000);
            }
        },
        [clock, lengthMs, subtitles, playerChannel, miscSettings.seekableTracks]
    );

    useSwipe({
        onSwipe: handleSwipe,
        distance: 50,
        ms: 500,
    });

    const isPausedDueToHoverRef = useRef<boolean>(undefined);

    const hoveredToken = useMemo(() => new HoveredToken(), []);

    const handleSubtitleMouseOver = useCallback(
        (e: React.MouseEvent) => {
            if (miscSettings.pauseOnHoverMode !== PauseOnHoverMode.disabled && videoRef.current?.paused === false) {
                playerChannel.pause();
                isPausedDueToHoverRef.current = true;
            }
            hoveredToken.handleMouseOver(e.nativeEvent);
        },
        [hoveredToken, miscSettings.pauseOnHoverMode, playerChannel]
    );

    const handleSubtitleMouseOut = useCallback(
        (e: React.MouseEvent) => hoveredToken.handleMouseOut(e.nativeEvent),
        [hoveredToken]
    );

    useEffect(() => {
        return keyBinder.bindMarkHoveredToken(
            (event, tokenStatus) => {
                const res = hoveredToken.parse();
                if (!res) return;
                void ensureStoragePersisted();
                event.preventDefault();
                event.stopImmediatePropagation();
                playerChannel.saveTokenLocal(res.track, res.token, tokenStatus, [], ApplyStrategy.ADD);
            },
            () => false
        );
    }, [hoveredToken, keyBinder, playerChannel]);

    useEffect(() => {
        return keyBinder.bindToggleHoveredTokenIgnored(
            (event) => {
                const res = hoveredToken.parse();
                if (!res) return;
                void ensureStoragePersisted();
                event.preventDefault();
                event.stopImmediatePropagation();
                playerChannel.saveTokenLocal(res.track, res.token, null, [TokenState.IGNORED], ApplyStrategy.TOGGLE);
            },
            () => false
        );
    }, [hoveredToken, keyBinder, playerChannel]);

    const inBetweenMobileOverlayAndBottomSubtitles = (e: React.MouseEvent<HTMLVideoElement>) => {
        if (!mobileOverlayRef.current || !bottomSubtitleContainerRef.current || !videoRef.current) {
            return;
        }

        const mobileOverlayRect = mobileOverlayRef.current.getBoundingClientRect();
        const subtitleContainerRect = bottomSubtitleContainerRef.current.getBoundingClientRect();
        const videoRect = videoRef.current.getBoundingClientRect();
        const bottom = videoRect.height + videoRect.y;
        const top = subtitleContainerRect.y;
        const left = Math.min(subtitleContainerRect.x, mobileOverlayRect.x);
        const right = Math.max(
            subtitleContainerRect.x + subtitleContainerRect.width,
            mobileOverlayRect.x + mobileOverlayRect.width
        );
        return e.clientY <= bottom && e.clientY >= top && e.clientX >= left && e.clientX <= right;
    };

    const handleVideoMouseOver = useCallback(
        (e: React.MouseEvent<HTMLVideoElement>) => {
            if (
                miscSettings.pauseOnHoverMode === PauseOnHoverMode.inAndOut &&
                isPausedDueToHoverRef.current &&
                !inBetweenMobileOverlayAndBottomSubtitles(e)
            ) {
                playerChannel.play();
                isPausedDueToHoverRef.current = false;
            }
        },
        [miscSettings.pauseOnHoverMode, playerChannel]
    );

    const { lastControlType, setLastControlType } = useLastScrollableControlType({
        isMobile,
        fetchLastControlType,
        saveLastControlType,
    });

    // If the video player is taking up the entire screen, then the subtitle player isn't showing
    // This code assumes some behavior in Player, namely that the subtitle player is automatically hidden
    // (and therefore the VideoPlayer takes up all the space) when there isn't enough room for the subtitle player
    // to be displayed.
    const notEnoughRoomForSubtitlePlayer =
        !subtitlePlayerHidden &&
        parent?.document?.body !== undefined &&
        parent.document.body.clientWidth === document.body.clientWidth;

    const elementForSubtitle = (subtitle: IndexedSubtitleModel, visible: boolean) => (
        <CachedShowingSubtitle
            key={subtitle.index}
            subtitle={subtitle}
            visible={visible}
            domCache={domCacheRef.current ?? getSubtitleDomCache()}
            renderHtml={getSubtitleHtml}
            onMouseOver={handleSubtitleMouseOver}
            onMouseOut={handleSubtitleMouseOut}
        />
    );

    const subtitleAlignmentForTrack = (track: number) => subtitleAlignments[track] ?? subtitleAlignments[0];
    const subtitleElementsWithAlignment = (alignment: SubtitleAlignment) => {
        const showing = showSubtitles.filter((subtitle) => subtitleAlignmentForTrack(subtitle.track) === alignment);
        const invisible = invisibleSubtitles.filter(
            (subtitle) => subtitleAlignmentForTrack(subtitle.track) === alignment
        );
        return mapSubtitlesForDisplay(showing, invisible, elementForSubtitle);
    };
    const topSubtitleElements = displaySubtitles ? subtitleElementsWithAlignment('top') : [];
    const bottomSubtitleElements = displaySubtitles ? subtitleElementsWithAlignment('bottom') : [];
    const mobileOverlayModel = () => {
        if (!isMobile || (playing() && mineIntervalStartTimestamp === undefined)) {
            return undefined;
        }

        const timestamp = clock.time({ maxMs: lengthMs });

        return {
            offset,
            playbackRate: videoRef.current?.playbackRate ?? 1,
            emptySubtitleTrack: subtitles.length === 0,
            recordingEnabled: true,
            recording: mineIntervalStartTimestamp !== undefined,
            previousSubtitleTimestamp:
                adjacentSubtitle(false, timestamp, subtitles, miscSettings.seekableTracks)?.originalStart ?? undefined,
            nextSubtitleTimestamp:
                adjacentSubtitle(true, timestamp, subtitles, miscSettings.seekableTracks)?.originalStart ?? undefined,
            currentTimestamp: timestamp,
            postMineAction: settings.clickToMineDefaultAction,
            subtitleDisplaying: showSubtitles.length > 0,
            subtitlesAreVisible: displaySubtitles,
            playModes: Array.from(playModes),
            themeType: settings.themeType,
        };
    };
    const baseBottomSubtitleOffset = !playing() && isMobile ? overlayContainerHeight : 0;
    const alertAnchor = subtitleAlignments[0] === 'top' ? 'bottom' : 'top';
    const resumePlaybackPrompt = useMemo(
        () => (
            <>
                {t('info.resumePlaybackPrompt', {
                    time: timeDurationDisplay(pendingPlaybackPosition ?? 0, pendingPlaybackPosition ?? 0, false),
                })}
                <Button
                    size="small"
                    color="inherit"
                    style={{ pointerEvents: 'auto', marginLeft: 12 }}
                    onClick={() => void playbackEngineRef.current?.resumePlaybackPosition()}
                >
                    {t('info.resumePlaybackButton')}
                </Button>
            </>
        ),
        [pendingPlaybackPosition, t]
    );

    if (!playerChannelSubscribed || lastControlType === undefined) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={`${classes.root} asbplayer-token-container`}
            tabIndex={-1}
        >
            <Alert
                open={resumePlaybackSnackbar.open}
                onClose={resumePlaybackSnackbar.close}
                onMouseEnter={resumePlaybackSnackbar.onMouseEnter}
                onMouseLeave={resumePlaybackSnackbar.onMouseLeave}
                autoHideDuration={0}
                disableAutoHide={true}
                useAppLogo={false}
                severity="info"
                anchor="bottom"
            >
                {resumePlaybackPrompt}
            </Alert>
            <Alert
                open={alert.open}
                disableAutoHide={alertDisableAutoHide}
                onClose={handleAlertClosed}
                notifications={alert.notifications}
                anchor={alertAnchor}
                useAppLogo={false}
            />
            <MobileVideoOverlay
                ref={mobileOverlayRef}
                model={mobileOverlayModel()}
                className={classes.mobileOverlay}
                anchor="bottom"
                tooltipsEnabled={true}
                initialControlType={lastControlType}
                flexDirection="column"
                onScrollToControlType={setLastControlType}
                onMineSubtitle={() => inferAndExecuteMiningBehavior(settings.clickToMineDefaultAction)}
                onOffset={handleOffsetChange}
                onPlaybackRate={handlePlaybackRateChange}
                onPlayModeSelected={handlePlayMode}
                onSeek={handleSeekByTimestamp}
                onToggleSubtitles={handleSubtitlesToggle}
            />
            <video
                preload="auto"
                controls={false}
                onClick={handleClick}
                onDoubleClick={handleDoubleClick}
                className={showCursor ? classes.video : `${classes.cursorHidden} ${classes.video}`}
                ref={videoRefCallback}
                src={videoFile}
                onMouseOver={handleVideoMouseOver}
            />
            {/* Optional blur mask overlay; constrained to the video bounds within the player container */}
            {blurOverlayVisible && <BlurOverlay anchorRef={containerRef} containerRef={videoRef} />}
            {/* this video is for getting the seek preview below */}
            {miscSettings.thumbnailPreview && (
                <video
                    src={videoFile}
                    muted
                    preload="none"
                    autoPlay={false}
                    style={{ position: 'absolute', left: '-9999px' }}
                    ref={(node) => {
                        hiddenVideoRef.current = node;
                        if (node) {
                            setHiddenVideoReady(true);
                        }
                    }}
                />
            )}
            {topSubtitleElements.length > 0 && (
                <SubtitleContainer
                    alignment={'top'}
                    subtitleSettings={subtitleSettings}
                    baseOffset={0}
                    subtitleZIndex={settings.subtitleAboveThumbnail}
                >
                    {topSubtitleElements}
                </SubtitleContainer>
            )}
            {bottomSubtitleElements.length > 0 && (
                <SubtitleContainer
                    ref={bottomSubtitleContainerRef}
                    alignment={'bottom'}
                    subtitleSettings={subtitleSettings}
                    baseOffset={baseBottomSubtitleOffset}
                    subtitleZIndex={settings.subtitleAboveThumbnail}
                >
                    {bottomSubtitleElements}
                </SubtitleContainer>
            )}
            <Controls
                videoWidth={videoWidth}
                videoHeight={videoHeight}
                mousePositionRef={mousePositionRef}
                clock={clock}
                length={lengthMs}
                audioTracks={audioTracks}
                selectedAudioTrack={selectedAudioTrack}
                subtitlesToggle={subtitles && subtitles.length > 0}
                subtitlesEnabled={displaySubtitles}
                offsetEnabled={true}
                offset={offset}
                playbackRate={videoRef.current?.playbackRate ?? 1}
                playbackRateEnabled={true}
                fullscreenEnabled={true}
                fullscreen={fullscreen}
                closeEnabled={!popOut}
                popOut={popOut}
                volumeEnabled={true}
                popOutEnabled={!isMobile}
                playModeEnabled={subtitles && subtitles.length > 0}
                playModes={playModes}
                previewEnabled={settings.thumbnailPreview}
                hideSubtitlePlayerToggleEnabled={
                    subtitles?.length > 0 && !popOut && !fullscreen && !notEnoughRoomForSubtitlePlayer
                }
                subtitlePlayerHidden={subtitlePlayerHidden}
                onPlay={handlePlay}
                onPause={handlePause}
                onSeek={handleSeek}
                onSeekPreview={handleSeekPreview}
                onAudioTrackSelected={handleAudioTrackSelected}
                onSubtitlesToggle={handleSubtitlesToggle}
                onFullscreenToggle={handleFullscreenToggle}
                onVolumeChange={handleVolumeChange}
                onOffsetChange={handleOffsetChange}
                onPlaybackRateChange={handlePlaybackRateChange}
                onPopOutToggle={handlePopOutToggle}
                onPlayMode={handlePlayMode}
                onClose={handleClose}
                onHideSubtitlePlayerToggle={handleHideSubtitlePlayerToggle}
                playbackPreferences={playbackPreferences}
                showOnMouseMovement={false}
                theaterModeToggleEnabled={!popOut && !fullscreen}
                theaterModeEnabled={appBarHidden}
                onTheaterModeToggle={handleTheaterModeToggle}
                subtitleAlignment={subtitleAlignments[0]}
                subtitleAlignmentEnabled={subtitleAlignments.length === 1}
                onSubtitleAlignment={handleSubtitleAlignment}
                hideToolbar={isMobile}
                onLoadFiles={popOut ? undefined : handleLoadFiles}
                onLoadSubtitles={popOut ? undefined : handleLoadSubtitles}
                blurOverlayEnabled={blurOverlayVisible}
                onBlurOverlayToggle={handleBlurOverlayToggle}
                timestampPreviewEnabled={!isMobile}
            />
        </div>
    );
}
